import type { NotificationChannel } from 'api-validations/notification'
import { getNotificationSettingsApi } from 'api-validations/uhura'
import { useCallback } from 'react'

import { usePanoramaQuery } from '../api-provider'
import { useAuth } from '../auth'

export const useNotificationSettings = () => {
  const { user } = useAuth()

  const query = usePanoramaQuery(
    getNotificationSettingsApi,
    { username: user?.username ?? '' },
    { enabled: user?.username !== undefined }
  )

  const getOrgChannels = useCallback(
    (orgCode: string) => {
      const orgSettings = query.data?.settings?.[orgCode]

      if (!orgSettings?.enabledChannels) {
        return []
      }

      return Object.entries(orgSettings.enabledChannels)
        .filter(([, enabled]) => enabled)
        .map(([channel]) => channel as NotificationChannel)
    },
    [query.data]
  )

  const isChannelEnabled = useCallback(
    (orgCode: string, channel: NotificationChannel) => {
      return query.data?.settings?.[orgCode]?.enabledChannels?.[channel] ?? false
    },
    [query.data]
  )

  const hasEnabledChannels = useCallback(() => {
    if (!query.data?.settings) return false

    return Object.values(query.data.settings).some((orgSettings) =>
      Object.values(orgSettings.enabledChannels).some((enabled) => enabled)
    )
  }, [query.data])

  return {
    notificationSettings: query.data?.settings,
    updatedAt: query.data?.updatedAt,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    getOrgChannels,
    isChannelEnabled,
    hasEnabledChannels
  }
}
